import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PendingCellEdits, PendingEdit } from '../../hooks/usePendingCellEdits';
import { useUpdateBudgetBaselines, useUpdateForecastVolumesBulk } from '../../hooks/api/budgetBillingConfig';
import { useUpdateOnTopCostsBulk } from '../../hooks/api/budgetOnTopCost';
import { queryClient } from '../../services/queryClient';
import { QueryKey } from '../../enums/QueryKey';
import FormSaveActions from './FormSaveActions';
import CellLegend from './CellLegend';
import { FORECAST_EDIT_KIND } from './ForecastVolumeGrid';
import { ON_TOP_EDIT_KINDS, ON_TOP_VALUE_KIND } from './OnTopCostsGrid';
import { BASELINE_EDIT_KIND, baselinePayload, forecastPayload, onTopPayload } from './overviewEditPayloads';

interface OverviewSaveBarProps {
    versionId: number;
    pending: PendingCellEdits;
}

// The save half of the Budget Overview (CALL-779): one bar for its three grids.
//
// Unlike the Billing Config and Prices & ABT pages, this one cannot be a single request — the page edits
// three separate resources (the version's baselines, its forecast volumes, its on-top costs), so Save
// fires one request per resource that actually has edits. Each is transactional on its own; there is no
// endpoint spanning all three, and inventing one to make a rare mixed save atomic is not worth the
// coupling it would add to three otherwise independent write paths.
//
// What that costs is spelled out here: each resource clears ITS OWN edits as it lands, so a partial
// failure leaves exactly the parts that did not land marked unsaved. Clearing all three together (or
// nothing) was worse than untidy — the grid then read as "nothing saved", and VERWERFEN, which the user
// reaches for next, wiped the markers of the request that HAD committed without rolling it back, so
// figures they asked to discard stayed in the version.
const OverviewSaveBar = ({ versionId, pending }: OverviewSaveBarProps) => {
    const { t } = useTranslation('budget');
    const [isSaving, setIsSaving] = useState(false);

    const { mutateAsync: saveBaselines } = useUpdateBudgetBaselines(versionId);
    const { mutateAsync: saveForecast } = useUpdateForecastVolumesBulk(versionId);
    const { mutateAsync: saveOnTop } = useUpdateOnTopCostsBulk(versionId);

    // The edits behind each request, kept alongside the payload so the request can clear precisely them.
    const groups = useMemo(() => {
        const edits = pending.edits;
        return {
            baselines: edits.filter((edit) => edit.kind === BASELINE_EDIT_KIND),
            forecast: edits.filter((edit) => edit.kind === FORECAST_EDIT_KIND),
            onTop: edits.filter((edit) => ON_TOP_EDIT_KINDS.includes(edit.kind)),
        };
    }, [pending.edits]);

    // The one constraint the Overview's endpoints enforce that its cells do not: a volume is `min(0)`.
    // Without this a pasted negative failed the forecast request with a raw ZodError while the other two
    // resources committed, and no cell was flagged — so Save is blocked the way the config pages block it.
    const firstIssue = useMemo(
        () =>
            groups.forecast.some((edit) => edit.value != null && Number(edit.value) < 0)
                ? t('validation.volumeNegative')
                : null,
        [groups.forecast, t]
    );

    const handleSave = useCallback(async () => {
        setIsSaving(true);

        // Each mutation reports its own failure (they all toast the server's message), so nothing here
        // has to look at the results — a request that resolves clears its own edits and one that rejects
        // leaves them pending, still marked, ready to retry. The invalidation is awaited BEFORE the
        // clear, or the grid would render the stale cache with no pending marks until the refetch landed.
        // It uses the object form, like every other Budget hook: a prefix match, and the only signature
        // react-query v5 still accepts (there, an array is read as a filters object with no queryKey,
        // which matches every query in the cache).
        const save = async (
            edits: PendingEdit[],
            run: () => Promise<unknown>,
            written: (result: unknown) => Set<number> | 'all',
            keys: unknown[][]
        ) => {
            if (!edits.length) return;
            pending.submit(edits);
            try {
                const result = await run();
                await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
                pending.settle(edits, written(result));
            } catch (error) {
                pending.settle(edits, new Set());
                throw error;
            }
        };

        await Promise.allSettled([
            save(
                groups.baselines,
                () => saveBaselines(baselinePayload(groups.baselines)),
                () => 'all',
                [[QueryKey.BudgetSummary, versionId]]
            ),
            save(
                groups.forecast,
                () => saveForecast(forecastPayload(groups.forecast, FORECAST_EDIT_KIND)),
                () => 'all',
                [[QueryKey.BudgetBillingConfigs], [QueryKey.BudgetSummary, versionId]]
            ),
            save(
                groups.onTop,
                () => saveOnTop(onTopPayload(groups.onTop, ON_TOP_VALUE_KIND)),
                // This endpoint reports what it wrote: a cost someone else deleted is skipped, and its
                // edits have to survive rather than vanish under a success toast.
                (result) => new Set(((result ?? []) as Array<{ id: number }>).map((cost) => Number(cost.id))),
                [
                    [QueryKey.BudgetOnTopCosts, versionId],
                    [QueryKey.BudgetSummary, versionId],
                ]
            ),
        ]);

        setIsSaving(false);
    }, [groups, versionId, saveBaselines, saveForecast, saveOnTop, pending]);

    // The unsaved-changes guard is NOT here: this bar is hidden on a read-only version, so the page owns
    // it (see UnsavedEditsPrompt in BudgetOverview).
    return (
        <FormSaveActions
            variant="sticky"
            canSave={pending.isDirty && !isSaving && !firstIssue}
            isDirty={pending.isDirty}
            isSaving={isSaving}
            onSave={handleSave}
            onCancel={pending.discard}
            firstIssue={firstIssue ?? undefined}
            pendingCount={pending.count}
            // Every cue this page can paint: Ist Budget and On Top Costs are plain editable cells,
            // Handlingdaten adds both the extrapolation blue and the manual-override bar.
            legend={<CellLegend cues={['editable', 'unsaved', 'manualOverride', 'extrapolated']} />}
        />
    );
};

export default OverviewSaveBar;
